import React, { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Divider,
} from "@mui/material";
import { Order, PaymentMethod } from "../../types";
import { CreateReturnRequest, OrderReturn, ReturnCondition } from "../../services/orderReturnService";
import { formatCurrencyBDT } from "../../utils/currency";
import { AccountPicker } from "../accounts/AccountPicker";
import {
    RETURN_REFUND_PAYMENT_METHODS,
    PAYMENT_METHOD_LABELS,
} from "../../utils/paymentMethods";

interface ProcessReturnDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: CreateReturnRequest) => Promise<void>;
    order: Order | null;
    existingReturns: OrderReturn[];
}

interface LineState {
    selected: boolean;
    quantity: number;
    condition: ReturnCondition;
}

export const ProcessReturnDialog: React.FC<ProcessReturnDialogProps> = ({
    open,
    onClose,
    onConfirm,
    order,
    existingReturns,
}) => {
    const [lines, setLines] = useState<Record<string, LineState>>({});
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [refundAmount, setRefundAmount] = useState(0);
    const [refundEdited, setRefundEdited] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("CASH");
    const [accountId, setAccountId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memoized so the fallback `[]` doesn't create a new array reference on
    // every render (which would otherwise defeat the useMemo hooks below).
    const items = useMemo(() => order?.items || [], [order?.items]);

    // Original subtotal is stable — order.items themselves are never
    // mutated by returns, only Order.total_amount/total_cost_price are.
    const originalSubtotal = useMemo(
        () => items.reduce((sum, i) => sum + Number(i.total_price), 0),
        [items],
    );
    const discountRatio =
        originalSubtotal > 0 ? (Number(order?.discount_amount) || 0) / originalSubtotal : 0;

    const alreadyReturned = useMemo(() => {
        const map: Record<string, number> = {};
        for (const ret of existingReturns) {
            for (const line of ret.items) {
                map[line.order_item_id] = (map[line.order_item_id] || 0) + line.quantity;
            }
        }
        return map;
    }, [existingReturns]);

    const remainingFor = (itemId: string, totalQty: number) =>
        Math.max(0, totalQty - (alreadyReturned[itemId] || 0));

    const handleOpen = () => {
        const initial: Record<string, LineState> = {};
        for (const item of items) {
            initial[item.item_id] = { selected: false, quantity: 1, condition: "RESELLABLE" };
        }
        setLines(initial);
        setReason("");
        setNotes("");
        setRefundAmount(0);
        setRefundEdited(false);
        setPaymentMethod("CASH");
        setAccountId(null);
        setError(null);
    };

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const updateLine = (itemId: string, patch: Partial<LineState>) => {
        setLines((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
    };

    const suggestedRefund = useMemo(() => {
        let total = 0;
        for (const item of items) {
            const line = lines[item.item_id];
            if (!line?.selected) continue;
            total += line.quantity * Number(item.unit_price) * (1 - discountRatio);
        }
        return Math.round(total * 100) / 100;
    }, [items, lines, discountRatio]);

    // Keep the refund field in sync with the live suggestion until staff
    // types into it directly — after that, their value wins.
    const effectiveRefund = refundEdited ? refundAmount : suggestedRefund;

    const selectedLines = items.filter((i) => lines[i.item_id]?.selected);

    const handleSubmit = async () => {
        if (selectedLines.length === 0) {
            setError("Select at least one item to return.");
            return;
        }
        if (!reason.trim()) {
            setError("A reason is required.");
            return;
        }
        if (effectiveRefund > 0 && !paymentMethod) {
            setError("Pick a refund payment method, or set the refund amount to 0.");
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await onConfirm({
                items: selectedLines.map((item) => ({
                    order_item_id: item.item_id,
                    quantity: lines[item.item_id].quantity,
                    condition: lines[item.item_id].condition,
                })),
                reason: reason.trim(),
                notes: notes.trim() || undefined,
                refund_amount: effectiveRefund,
                refund_payment_method: effectiveRefund > 0 ? paymentMethod : undefined,
                refund_account_id: effectiveRefund > 0 ? accountId : undefined,
            });
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.data?.detail || err?.message || "Failed to process return.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            TransitionProps={{ onEnter: handleOpen }}
        >
            <DialogTitle>Return Items</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Product</TableCell>
                                    <TableCell align="center">Qty</TableCell>
                                    <TableCell align="center">Condition</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    const remaining = remainingFor(item.item_id, item.quantity);
                                    const line = lines[item.item_id] || {
                                        selected: false,
                                        quantity: 1,
                                        condition: "RESELLABLE" as ReturnCondition,
                                    };
                                    return (
                                        <TableRow key={item.item_id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={line.selected}
                                                    disabled={remaining === 0}
                                                    onChange={(e) =>
                                                        updateLine(item.item_id, {
                                                            selected: e.target.checked,
                                                            quantity: Math.min(
                                                                line.quantity,
                                                                remaining,
                                                            ) || 1,
                                                        })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {item.product?.name || item.product_id}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {remaining} of {item.quantity} still returnable
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={line.quantity}
                                                    disabled={!line.selected}
                                                    onChange={(e) =>
                                                        updateLine(item.item_id, {
                                                            quantity: Math.max(
                                                                1,
                                                                Math.min(
                                                                    Number(e.target.value) || 1,
                                                                    remaining,
                                                                ),
                                                            ),
                                                        })
                                                    }
                                                    inputProps={{ min: 1, max: remaining }}
                                                    sx={{ width: 80 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={line.condition}
                                                    disabled={!line.selected}
                                                    onChange={(e) =>
                                                        updateLine(item.item_id, {
                                                            condition: e.target
                                                                .value as ReturnCondition,
                                                        })
                                                    }
                                                    sx={{ width: 150 }}
                                                >
                                                    <MenuItem value="RESELLABLE">
                                                        Resellable — restock
                                                    </MenuItem>
                                                    <MenuItem value="DAMAGED">
                                                        Damaged — don't restock
                                                    </MenuItem>
                                                </TextField>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TextField
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        required
                        placeholder="Wrong item, changed mind, defective, ..."
                    />
                    <TextField
                        label="Notes (Optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                    />

                    <Divider />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            Suggested refund (revenue reversed, less proportional discount):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {formatCurrencyBDT(suggestedRefund)}
                        </Typography>
                    </Box>
                    <TextField
                        label="Refund Amount"
                        type="number"
                        value={effectiveRefund}
                        onChange={(e) => {
                            setRefundEdited(true);
                            setRefundAmount(Number(e.target.value));
                        }}
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Edit to apply a restocking fee, give goodwill, or leave at 0 to record the return without a refund now"
                    />
                    {effectiveRefund > 0 && (
                        <>
                            <TextField
                                select
                                label="Refund Payment Method"
                                value={paymentMethod}
                                onChange={(e) =>
                                    setPaymentMethod(e.target.value as PaymentMethod)
                                }
                                fullWidth
                                required
                            >
                                {RETURN_REFUND_PAYMENT_METHODS.map((method) => (
                                    <MenuItem key={method} value={method}>
                                        {PAYMENT_METHOD_LABELS[method]}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <AccountPicker
                                value={accountId}
                                onChange={setAccountId}
                                helperText="Which cash/bank account this refund came out of"
                            />
                        </>
                    )}

                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={saving || selectedLines.length === 0}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                    {saving ? "Processing..." : "Process Return"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
